const FALLBACK_TIMEZONE = "UTC";

export const DASHBOARD_DATE_PRESETS = Object.freeze({
  TODAY: "today",
  YESTERDAY: "yesterday",
  THIS_WEEK: "this_week",
  LAST_7_DAYS: "last_7_days",
  THIS_MONTH: "this_month",
  LAST_MONTH: "last_month",
  QUARTER: "quarter",
  YEAR: "year",
  CUSTOM: "custom",
});

export const DEFAULT_DASHBOARD_FILTERS = Object.freeze({
  time: {
    datePreset: DASHBOARD_DATE_PRESETS.LAST_7_DAYS,
    startDate: null,
    endDate: null,
    timezone: FALLBACK_TIMEZONE,
  },
  operational: {
    province: null,
    route: null,
    routeCorridor: null,
    moveType: null,
    vehicleType: null,
    vehicleId: null,
    driverId: null,
    teamId: null,
    operationalStatus: null,
  },
  commercial: {
    leadSource: null,
    campaign: null,
    quoteStatus: null,
    bookingStatus: null,
    paymentStatus: null,
  },
  customer: {
    customerSegment: null,
    budgetRange: null,
  },
});

const FILTER_GROUP_KEYS = Object.freeze(
  Object.keys(DEFAULT_DASHBOARD_FILTERS).filter((key) => key !== "time")
);

export function createDefaultDashboardFilters() {
  return mergeDashboardFilters(DEFAULT_DASHBOARD_FILTERS, {
    time: {
      timezone: getBrowserTimezone(),
    },
  });
}

export function mergeDashboardFilters(baseFilters, updates = {}) {
  const base = baseFilters || createDefaultDashboardFilters();

  return {
    time: {
      ...DEFAULT_DASHBOARD_FILTERS.time,
      ...base.time,
      ...(updates.time || {}),
    },
    operational: {
      ...DEFAULT_DASHBOARD_FILTERS.operational,
      ...base.operational,
      ...(updates.operational || {}),
    },
    commercial: {
      ...DEFAULT_DASHBOARD_FILTERS.commercial,
      ...base.commercial,
      ...(updates.commercial || {}),
    },
    customer: {
      ...DEFAULT_DASHBOARD_FILTERS.customer,
      ...base.customer,
      ...(updates.customer || {}),
    },
  };
}

export function updateDashboardFilterValue(filters, group, key, value) {
  if (!Object.prototype.hasOwnProperty.call(DEFAULT_DASHBOARD_FILTERS, group)) {
    return mergeDashboardFilters(filters);
  }

  return mergeDashboardFilters(filters, {
    [group]: {
      [key]: normalizeEmptyFilterValue(value),
    },
  });
}

export function resetDashboardFilters(overrides = {}) {
  return mergeDashboardFilters(createDefaultDashboardFilters(), overrides);
}

export function serializeDashboardFilters(filters = createDefaultDashboardFilters()) {
  const normalized = mergeDashboardFilters(createDefaultDashboardFilters(), filters);
  const params = {};

  appendQueryParam(params, "datePreset", normalized.time.datePreset);
  appendQueryParam(params, "startDate", normalized.time.startDate);
  appendQueryParam(params, "endDate", normalized.time.endDate);
  appendQueryParam(params, "timezone", normalized.time.timezone);

  FILTER_GROUP_KEYS.forEach((group) => {
    Object.entries(normalized[group]).forEach(([key, value]) => {
      appendQueryParam(params, key, value);
    });
  });

  return params;
}

export function coerceLegacyDateRangeToFilters(dateRange, filters) {
  if (isDashboardFilterObject(dateRange)) {
    return mergeDashboardFilters(filters, dateRange);
  }

  if (dateRange === null || dateRange === undefined) {
    return mergeDashboardFilters(filters);
  }

  const numberRange = Number(dateRange);
  if (!Number.isFinite(numberRange)) {
    return mergeDashboardFilters(filters);
  }

  if (numberRange <= 1) {
    return mergeDashboardFilters(filters, {
      time: { datePreset: DASHBOARD_DATE_PRESETS.TODAY },
    });
  }

  if (numberRange <= 7) {
    return mergeDashboardFilters(filters, {
      time: { datePreset: DASHBOARD_DATE_PRESETS.LAST_7_DAYS },
    });
  }

  if (numberRange <= 31) {
    return mergeDashboardFilters(filters, {
      time: { datePreset: DASHBOARD_DATE_PRESETS.THIS_MONTH },
    });
  }

  return mergeDashboardFilters(filters, {
    time: { datePreset: DASHBOARD_DATE_PRESETS.YEAR },
  });
}

export function getLegacySelectedTimeRange(filters) {
  const preset = filters?.time?.datePreset;

  if (preset === DASHBOARD_DATE_PRESETS.TODAY) return 1;
  if (preset === DASHBOARD_DATE_PRESETS.LAST_7_DAYS) return 7;
  if (preset === DASHBOARD_DATE_PRESETS.THIS_MONTH) return 30;
  if (preset === DASHBOARD_DATE_PRESETS.YEAR) return 365;
  return 30;
}

function appendQueryParam(params, key, value) {
  const normalized = normalizeEmptyFilterValue(value);
  if (normalized === null || normalized === undefined) return;

  params[key] = Array.isArray(normalized) ? normalized.join(",") : normalized;
}

function normalizeEmptyFilterValue(value) {
  if (value === "" || value === "all") return null;
  if (Array.isArray(value)) {
    const normalized = value
      .map((item) => normalizeEmptyFilterValue(item))
      .filter((item) => item !== null && item !== undefined);
    return normalized.length > 0 ? normalized : null;
  }
  return value;
}

function isDashboardFilterObject(value) {
  return Boolean(
    value &&
      typeof value === "object" &&
      (value.time || value.operational || value.commercial || value.customer)
  );
}

function getBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || FALLBACK_TIMEZONE;
  } catch (_) {
    return FALLBACK_TIMEZONE;
  }
}
